import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { roleHome } from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import connerSeal from '../assets/images/conner-seal.jpg';

// What login() sends where a queue ticket used to go. The server ignores
// it — sign-in has no ticket step any more (server lib/passwordHashing.ts).
const QUEUE_DISABLED_TICKET = 'queue-disabled';

// Shown once after a successful login, before landing in the system.
const SPLASH_DURATION_MS = 8000;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  // Set only when the server says the password was right but the address
  // is unconfirmed — the one case where offering a resend helps.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Set right after a successful login instead of navigating immediately —
  // holds the splash screen up for SPLASH_DURATION_MS before continuing on
  // to wherever this role actually lands.
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);
  // Drives the splash's linear progress bar — 0 to 100 over
  // SPLASH_DURATION_MS, purely cosmetic (the navigate timer below is what
  // actually controls when the splash ends).
  const [splashProgress, setSplashProgress] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const passwordResetSuccess = Boolean((location.state as { passwordResetSuccess?: boolean } | null)?.passwordResetSuccess);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password, QUEUE_DISABLED_TICKET);
      // Hands off to the credits splash rather than navigating straight
      // through — the effect below holds it for SPLASH_DURATION_MS and
      // then routes on by role.
      setPendingRoute(roleHome[user.role]);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      // The password was right; the address just hasn't been confirmed.
      // Offer another link rather than leaving them re-typing a password
      // that was correct — the first link may well have gone to spam.
      setNeedsVerification(err?.code === 'EMAIL_NOT_VERIFIED');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await apiService.resendVerificationEmail(email);
      // Deliberately vague, matching the server, which answers the same
      // way whether or not the address is registered.
      setResendMessage('If that address still needs confirming, a new link is on its way. Check your spam folder too.');
    } catch (err: any) {
      setResendMessage(err?.message || 'Could not send the link. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (!pendingRoute) return;
    const startedAt = Date.now();
    const progressInterval = setInterval(() => {
      setSplashProgress(Math.min(100, Math.round(((Date.now() - startedAt) / SPLASH_DURATION_MS) * 100)));
    }, 100);
    const timer = setTimeout(() => navigate(pendingRoute), SPLASH_DURATION_MS);
    return () => {
      clearInterval(progressInterval);
      clearTimeout(timer);
    };
  }, [pendingRoute, navigate]);

  if (pendingRoute) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <img
            src={connerSeal}
            alt="Municipality of Conner, Apayao seal"
            width={150}
            height={150}
            style={{ display: 'block', margin: '0 auto 1rem', borderRadius: '50%' }}
          />
          {/* No hardcoded colour here — these inherit .login-card > p,
              which is light on the dark purple theme and dark on the
              avocado one. They were pinned to black, which left them
              nearly unreadable against the purple card. */}
          <h2>Enhanced Conner Educational Assistance Program (ECEAP)</h2>
          <p style={{ marginBottom: '2rem' }}>All rights reserved &copy; MP_Culili</p>
          <div className="login-splash-progress" aria-hidden="true">
            <div className="login-splash-progress-bar" style={{ width: `${splashProgress}%` }} />
          </div>
          <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.8rem' }}>Loading... {splashProgress}%</p>
        </div>
      </div>
    );
  }

  // The sign-in form. There is no ticket step any more — see server lib/passwordHashing.ts.
  return (
    <div className="login-container">
      <div className="login-card">
        <img
          src={connerSeal}
          alt="Municipality of Conner, Apayao seal"
          width={150}
          height={150}
          style={{ display: 'block', margin: '0 auto 1rem', borderRadius: '50%' }}
        />
        <h2>Welcome back</h2>
        <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
          Enhanced Conner Educational Assistance Program (ECEAP)
        </p>
        <p>Sign in to access the scholarship management portal</p>

        {passwordResetSuccess && !error && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              color: '#34D399',
              fontSize: '0.9rem'
            }}
          >
            Your password has been reset — sign in with your new password.
          </div>
        )}

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            {error}
            {/* Only when the password was right and the address simply
                isn't confirmed. Without this a student whose link went
                to spam, or expired after seven days, has no way back in
                at all. */}
            {needsVerification && (
              <div style={{ marginTop: '0.75rem' }}>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={resending}
                  onClick={handleResendVerification}
                >
                  {resending ? 'Sending...' : 'Resend the confirmation link'}
                </button>
                {resendMessage && (
                  <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.85rem' }}>{resendMessage}</p>
                )}
              </div>
            )}
          </div>
        )}

        <form className="form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-with-affix">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ paddingRight: '3.5rem' }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--login-toggle-color)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <Link to="/forgot-password" style={{ fontSize: '0.85rem', alignSelf: 'flex-end' }}>
              Forgot password?
            </Link>
          </div>
          <button className="btn btn-primary btn-lg" type="submit" disabled={isLoading} style={{ width: '100%' }}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p style={{ margin: '1.5rem 0 0', textAlign: 'center', fontSize: '0.9rem' }}>
          New student? <Link to="/register">Create an account</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
